import React, { useContext } from "react";

import GeneralPageContext from "../../templates/GeneralPageContext";
import { isLightBackground } from "../Background/Background";

import { BranchAndApplicationTeaser } from "../../content-elements/teaser/BranchAndApplicationTeaser/BranchAndApplicationTeaser";
import { CallToAction } from "../../content-elements/CallToAction";
import { CameraFinder } from "../../content-elements/CameraFinder/CameraFinder";
import { CameraTeaser } from "../../content-elements/teaser/CameraTeaser/CameraTeaser";
import { StandardModelbasedTeaser } from "../../content-elements/teaser/StandardModelbasedTeaser";
import { ContactPersonBlock } from "../../content-elements/ContactPersonBlock";
import { ContentTeaser } from "../../content-elements/teaser/ContentTeaser/ContentTeaser";
import { ContentTeaserMediaText } from "../../content-elements/teaser/ContentTeaserMediaText/ContentTeaserMediaText";
import { Faq } from "../../content-elements/Faq";
import { MediaFullWidth } from "../../content-elements/MediaFullWidth/MediaFullWidth";
import { MediaBlurb } from "../../content-elements/MediaBlurb";
import { MediaSlider } from "../../content-elements/MediaSlider/MediaSlider";
import { MediaForContact } from "../../content-elements/MediaForContact";
import { MediaText } from "../../content-elements/MediaText";
import { MediaTextAdvanced } from "../../content-elements/MediaTextAdvanced";
import { NestedImage } from "../../content-elements/nested/Image";
import { NestedLinkBox } from "../../content-elements/nested/LinkBox";
import { NestedTable } from "../../content-elements/nested/Table";
import { NestedText } from "../../content-elements/nested/Text";
import { TestimonialBlock } from "../../content-elements/TestimonialBlock";
import { NestedDownloadList } from "../../content-elements/nested/DownloadList";
import { Text } from "../../content-elements/Text";
import { TrustLogo } from "../../content-elements/TrustLogo";
import { withTeaserConfig } from "../../hoc/withTeaserConfig/withTeaserConfig";
import { ContactDepartment } from "../../content-elements/ContactDepartment/ContactDepartment";
import { ContactPersonDivision } from "../../content-elements/ContactPersonDivision/ContactPersonDivision";
import { CameraTechnicalDetail } from "../../content-elements/CameraTechnicalDetail/CameraTechnicalDetail";
import ElementContext from "../ElementContext";

const MEDIA_LAYOUT_STANDARD = "standard_without_lightbox";

export function ContentElementRenderer({
    items = [],
    locale,
    children,
    hasLightBackground,
    config,
    layoutReduced = false,
    additionalContext = {},
    withGallery = MEDIA_LAYOUT_STANDARD 
}) {
    const { id: pageId } = useContext(GeneralPageContext);
    const contentElementMap = {
        DatoCmsGlobalBlock: "GlobalBlock",
        DatoCmsCta: CallToAction,
        DatoCmsContactPersonBlock: ContactPersonBlock,
        DatoCmsText: Text,
        DatoCmsMediaText: MediaText,
        DatoCmsTrustLogo: TrustLogo,
        DatoCmsMediaSlider: MediaSlider,
        DatoCmsCameraFinder: CameraFinder,
        DatoCmsMediaFullWidth: MediaFullWidth,
        DatoCmsMediaBlurb: MediaBlurb,
        DatoCmsFaqBlock: Faq,
        DatoCmsTestimonialBlock: TestimonialBlock,
        DatoCmsMediaTextAdvanced: MediaTextAdvanced,
        DatoCmsMediaForContact: MediaForContact,
        DatoCmsContactDepartment: ContactDepartment,
        DatoCmsContactPersonDivision: ContactPersonDivision,
        DatoCmsContactPersonDivisionShort: ContactPersonDivision,
        DatoCmsCameraTechnicalDetail: CameraTechnicalDetail,

        DatoCmsDownloadList: NestedDownloadList,

        // --- Nested Elements ---
        DatoCmsNestedImage: NestedImage,
        DatoCmsNestedTable: NestedTable,
        DatoCmsNestedText: NestedText,
        DatoCmsNestedTextHint: NestedText,
        DatoCmsNestedTextNotice: NestedText,
        DatoCmsNestedLinkBox: NestedLinkBox,

        // --- Teaser ---
        DatoCmsNewsTeaser: withTeaserConfig(StandardModelbasedTeaser, {
            key: "newsTeaser"
        }),
        DatoCmsJobAdvertisementTeaser: withTeaserConfig(
            StandardModelbasedTeaser,
            {
                key: "vacancyTeaser"
            }
        ),
        DatoCmsFairTeaser: withTeaserConfig(StandardModelbasedTeaser, {
            key: "fairTeaser"
        }),
        DatoCmsSeminarTeaser: withTeaserConfig(StandardModelbasedTeaser, {
            key: "seminarTeaser"
        }),
        DatoCmsApplicationAndBranchTeaser: withTeaserConfig(
            BranchAndApplicationTeaser,
            { key: "applicationTeaser" }
        ),

        DatoCmsContentTeaserMediaText: ContentTeaserMediaText,
        DatoCmsContentTeaser: ContentTeaser,

        DatoCmsCameraTeaser: CameraTeaser,
        DatoCmsCameraAccessorieTeaser: CameraTeaser
    };
    return (
        <>
            {children}

            {items.map((contentElement) => {
                if (!contentElement.id) {
                    console.warn(
                        `The content element of type "${contentElement?.type}" on page ID "${pageId}" needs to have an ID.`,
                        contentElement
                    );
                    return null;
                }

                const elementType = contentElement?.type;

                if (!elementType) {
                    console.warn(
                        "The content element needs to have a type attribute!.",
                        contentElement
                    );
                    return null;
                }

                const ContentElement = contentElementMap[elementType] ?? null;

                if (!ContentElement) {
                    console.warn(
                        `There was no internal component found for content element type '${elementType}'.`,
                        contentElement
                    );
                    return null;
                }

                contentElement.background =
                    contentElement.background || "bg_white";

                if (ContentElement === "GlobalBlock") {
                    return (
                        <ContentElementRenderer
                            key={contentElement.id}
                            locale={locale}
                            items={
                                contentElement?.globalContentBlock
                                    ?.contentBlock ?? []
                            }
                            config={config}
                            withGallery={withGallery}
                        />
                    );
                }

                const initialContext = {
                    isLightBg: isLightBackground(contentElement?.background),
                    background: contentElement?.background,
                    ...additionalContext
                };

                return (
                    <ElementContext.Provider
                        value={initialContext}
                        key={contentElement.id}
                    >
                        <ContentElement
                            locale={locale}
                            data={contentElement}
                            type={elementType}
                            config={config}
                            hasLightBackground={hasLightBackground}
                            layoutReduced={layoutReduced}
                            withGallery={withGallery}
                        />
                    </ElementContext.Provider>
                );
            })}
        </>
    );
}
